import Link from "next/link";
import { loginAction } from "@/app/login/actions";
import { PasswordField } from "@/components/auth/password-field";

/** Dark auth shell matching StrategyIQ screenshots (forced, independent of app theme). */
export const authFieldClass =
  "w-full rounded-xl border border-[#37373f] bg-[#1f1f24] px-3.5 py-3 text-[15px] text-[#f3f3f5] placeholder:text-[#8b8b96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/25";

export const authOutlineBtnClass =
  "flex w-full items-center justify-center gap-2 rounded-xl border border-[#37373f] bg-transparent px-3.5 py-3 text-[15px] font-medium text-[#f3f3f5] transition-colors hover:bg-[#222228] disabled:opacity-45";

export const authPrimaryBtnClass =
  "flex w-full items-center justify-center rounded-xl bg-white px-3.5 py-3 text-[15px] font-semibold text-zinc-950 transition-colors hover:bg-zinc-200 disabled:opacity-50";

export function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a0a0c] px-4 py-10 text-[#f3f3f5]">
      <div className="w-full max-w-[400px]">{children}</div>
    </div>
  );
}

export function AuthBrand() {
  return (
    <div className="mb-8 flex flex-col items-center text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl siq-brand-mark shadow-lg">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
          <path d="M20 2v4" />
          <path d="M22 4h-4" />
          <circle cx="4" cy="20" r="2" />
        </svg>
      </div>
      <h1 className="text-[1.75rem] font-semibold tracking-tight text-[#f3f3f5] sm:text-[2rem]">
        StrategyIQ
      </h1>
    </div>
  );
}

export function OrDivider() {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="h-px flex-1 bg-[#2a2a2f]" />
      <span className="text-[13px] font-medium text-[#8b8b96]">Oder</span>
      <div className="h-px flex-1 bg-[#2a2a2f]" />
    </div>
  );
}

export function LegalFooter() {
  return (
    <p className="pt-3 text-center text-[12px] text-[#8b8b96]">
      <a href="#nutzungsbedingungen" className="hover:text-[#b4b4be]">
        Nutzungsbedingungen
      </a>
      <span className="mx-1.5 text-[#5e5e68]">|</span>
      <a href="#datenschutz" className="hover:text-[#b4b4be]">
        Datenschutzrichtlinie
      </a>
    </p>
  );
}

function SocialIcon({ kind }: { kind: "google" | "apple" | "microsoft" }) {
  if (kind === "google") {
    return (
      <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
        <path fill="#EA4335" d="M12 10.2v3.6h5.1c-.2 1.2-.9 2.2-1.9 2.9l3.1 2.4c1.8-1.7 2.8-4.1 2.8-7 0-.7-.1-1.3-.2-1.9H12z" />
        <path fill="#34A853" d="M6.6 14.3l-.8.6-2.5 1.9C4.9 19.5 8.2 21.5 12 21.5c2.7 0 4.9-.9 6.5-2.4l-3.1-2.4c-.9.6-2 .9-3.4.9-2.6 0-4.8-1.7-5.6-4.1z" />
        <path fill="#4A90E2" d="M3.3 7.2C2.5 8.7 2 10.3 2 12s.5 3.3 1.3 4.8l3.3-2.5C6.2 13.4 6 12.7 6 12c0-.7.2-1.4.5-2.1L3.3 7.2z" />
        <path fill="#FBBC05" d="M12 5.5c1.5 0 2.8.5 3.8 1.5l2.8-2.8C17 2.4 14.7 1.5 12 1.5 8.2 1.5 4.9 3.5 3.3 7.2l3.2 2.5C7.2 7.2 9.4 5.5 12 5.5z" />
      </svg>
    );
  }
  if (kind === "apple") {
    return (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M16.4 12.7c0-2.1 1.7-3.1 1.8-3.2-1-1.4-2.5-1.6-3.1-1.6-1.3-.1-2.5.8-3.2.8-.7 0-1.7-.7-2.9-.7-1.5 0-2.8.9-3.6 2.2-1.5 2.7-.4 6.6 1.1 8.8.7 1 1.6 2.2 2.7 2.2 1.1 0 1.5-.7 2.8-.7s1.7.7 2.8.7c1.2 0 1.9-1 2.6-2 .8-1.2 1.1-2.3 1.1-2.4-.1 0-2.2-.8-2.2-3.4zM14.5 6.5c.6-.7 1-1.7.9-2.7-.9 0-1.9.6-2.5 1.3-.6.6-1.1 1.7-.9 2.6 1 .1 1.9-.5 2.5-1.2z" />
      </svg>
    );
  }
  return (
    <svg className="h-5 w-5" viewBox="0 0 23 23" aria-hidden>
      <path fill="#f35325" d="M1 1h10v10H1z" />
      <path fill="#81bc06" d="M12 1h10v10H12z" />
      <path fill="#05a6f0" d="M1 12h10v10H1z" />
      <path fill="#ffba08" d="M12 12h10v10H12z" />
    </svg>
  );
}

function buildLoginHref(params: Record<string, string | undefined>) {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) q.set(key, value);
  }
  const s = q.toString();
  return s ? `/login?${s}` : "/login";
}

export type LoginViewProps = {
  mode: "password" | "otp";
  email: string;
  nextPath: string;
  error: string | null;
  info: string | null;
  failed: boolean;
  moreOpen: boolean;
  devCode: string | null;
};

/**
 * Server-rendered login — no client JS required for Weiter / OTP / forms.
 */
export function LoginView({
  mode,
  email,
  nextPath,
  error,
  info,
  failed,
  moreOpen,
  devCode,
}: LoginViewProps) {
  if (mode === "otp") {
    return (
      <AuthShell>
        <AuthBrand />
        <form action="/api/auth/otp/verify-form" method="post" className="space-y-3">
          <input type="hidden" name="email" value={email} />
          <input type="hidden" name="next" value={nextPath} />
          <p className="text-center text-sm text-[#b4b4be]">
            Code an <span className="font-medium text-[#f3f3f5]">{email}</span>{" "}
            gesendet (1 Stunde gültig).
          </p>

          {/* Single field; digits appear side-by-side via tracking */}
          <input
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            name="code"
            autoComplete="one-time-code"
            placeholder="• • • • • •"
            aria-label="6-stelliger Code"
            className={`${authFieldClass} text-center font-mono text-[1.75rem] tracking-[0.55em]`}
            autoFocus
          />

          {devCode && (
            <p className="rounded-xl border border-[#2a2a2f] bg-[#1c1c20] px-3 py-2 text-[12px] text-[#8b8b96]">
              Dev-Code (Mail offline):{" "}
              <span className="font-mono text-[#f3f3f5]">{devCode}</span>
            </p>
          )}
          {info && <p className="text-center text-sm text-[#65c985]">{info}</p>}
          {error && <p className="text-center text-sm text-[#ed5d64]">{error}</p>}

          <button type="submit" className={authPrimaryBtnClass}>
            Weiter
          </button>

          <button
            type="submit"
            formNoValidate
            formAction="/api/auth/otp/request-form"
            formMethod="post"
            className={`${authOutlineBtnClass} !border-transparent text-[#b4b4be] hover:text-[#f3f3f5]`}
          >
            E-Mail erneut senden
          </button>

          <OrDivider />

          <a
            href={buildLoginHref({ email: email || undefined })}
            className={authOutlineBtnClass}
          >
            Mit Passwort fortfahren
          </a>

          <LegalFooter />
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <AuthBrand />
      {/*
        "Weiter" is deliberately the first submit button in tree order, and the
        layout is restored with flex `order` rather than by moving it back.
        Pressing Enter in a field triggers implicit submission, which activates
        the form's *default button* - the first submit button in the DOM. While
        "Passwort vergessen?" held that position, hitting Enter on the login
        form posted to /api/auth/forgot-redirect and sent people to the
        password-reset page without ever checking their credentials.

        `gap-3` rather than `space-y-3`: space-y puts margins on later siblings
        in DOM order, which lands them in the wrong place once `order` moves
        things around. gap is order-independent.
      */}
      <form action={loginAction} className="flex flex-col gap-3">
        <button type="submit" className={`order-2 ${authPrimaryBtnClass}`}>
          Weiter
        </button>

        <input type="hidden" name="next" value={nextPath} />

        <input
          type="email"
          name="email"
          required
          autoComplete="email"
          placeholder="E-Mail-Adresse"
          defaultValue={email}
          className={authFieldClass}
          autoFocus
        />

        <PasswordField name="password" placeholder="Passwort" autoComplete="current-password" />

        <div className="flex w-full justify-start">
          <button
            type="submit"
            formNoValidate
            formAction="/api/auth/forgot-redirect"
            formMethod="post"
            className="text-left text-[13px] font-medium text-[#b4b4be] hover:text-[#f3f3f5] hover:underline"
          >
            Passwort vergessen?
          </button>
        </div>

        {info && <p className="text-left text-sm text-[#65c985]">{info}</p>}
        {error && <p className="text-left text-sm text-[#ed5d64]">{error}</p>}

        {/*
          Everything below "Weiter" is grouped so a single `order-3` keeps it
          after the button; OrDivider and LegalFooter take no className of
          their own. The inner gap-3 reproduces the spacing these had as direct
          children of the form.
        */}
        <div className="order-3 flex flex-col gap-3">
          {/* OTP only after failed password attempt */}
          {failed && (
            <button
              type="submit"
              formNoValidate
              formAction="/api/auth/otp/request-form"
              formMethod="post"
              className={authOutlineBtnClass}
            >
              Mit Einmalcode anmelden
            </button>
          )}

          <OrDivider />

          <details className="group" {...(moreOpen ? { open: true } : {})}>
            <summary
              className={`${authOutlineBtnClass} list-none [&::-webkit-details-marker]:hidden cursor-pointer select-none`}
            >
              <span className="group-open:hidden">Weitere Anmeldeoptionen</span>
              <span className="hidden group-open:inline">Weniger Anmeldeoptionen</span>
            </summary>
            <div className="mt-2.5 space-y-2.5">
              <a href="/developer-access" className={authOutlineBtnClass}>
                Developer Login
              </a>
              <button type="button" className={authOutlineBtnClass} disabled>
                <SocialIcon kind="google" />
                Mit Google fortfahren
              </button>
              <button type="button" className={authOutlineBtnClass} disabled>
                <SocialIcon kind="apple" />
                Mit Apple fortfahren
              </button>
              <button type="button" className={authOutlineBtnClass} disabled>
                <SocialIcon kind="microsoft" />
                Mit Microsoft fortfahren
              </button>
            </div>
          </details>

          <p className="pt-2 text-center text-sm text-[#8b8b96]">
            Noch kein Konto?{" "}
            <Link href="/register" className="font-medium text-[#f3f3f5] underline">
              Registrieren
            </Link>
          </p>
          <LegalFooter />
        </div>
      </form>
    </AuthShell>
  );
}
